import React, {useState} from "react"
import Papa from "papaparse"
import {isEqual, chunk, uniqBy} from "lodash"
import moment from "moment-timezone"

import {InjectIntl, injectIntl} from "@irati/utils/lang"
import {promiseSerialAll, waitPromise} from "@irati/utils/promise"
import {User, UserInput, UserDateOfBirth} from "@irati/types"
import {
  DONT_INCLUDE,
  csvRowToUser,
  getUserCSVHeaders,
  getUserCSVRows,
  FIRSTNAME,
  LASTNAME,
  EMAIL,
  DATE_OF_BIRTH,
  EMPLOYMENT_START_DATE,
  EXTERNAL_ID
} from "@irati/admin/helpers/userCSV"
import {getErrorMessage} from "@irati/utils/lang/formatError"
import {setIntDate} from "@irati/utils/dob"
import usersService from "@irati/identity/services/users"

import EditPeopleCSV from "../../../components/Users/EditUsers/EditUsersCSV"
import BackLink from "../../../components/Users/BackLink"
import AddPeopleHeaders from "../../../components/Users/AddUsers/AddPeopleHeaders/AddPeopleHeaders"
import EditPeopleFailed from "../../../components/Users/EditUsers/EditUsersFailed"
import SuccessModal from "./SuccessModal"

export enum UpdatePeopleSteps {
  UPDATE_PEOPLE_CSV = "update_people-csv",
  UPDATE_PEOPLE_HEADERS = "update_people_headers",
  UPDATE_PEOPLE_FAILED = "update_people-failed"
}

interface Props extends InjectIntl {}

const EditMultiplePeople: React.FC<Props> = ({formatMessage}) => {
  const [currentStep, setCurrentStep] = useState(UpdatePeopleSteps.UPDATE_PEOPLE_CSV)
  const [updated, setUpdated] = useState<User[]>([])
  const [updatedNo, setUpdatedNo] = useState<number>(0)
  const [notUpdated, setNotUpdated] = useState<User[]>([])
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [csv, setCsv] = useState<string[]>([])
  const [headers, setHeaders] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const handleOnDrop = (acceptedFiles) => {
    let parsedRows: any[] = []
    Papa.parse(acceptedFiles[0], {
      header: false,
      skipEmptyLines: true,
      step: (row: any) => {
        if (row.data[0].find((value) => value.trim() !== "")) {
          parsedRows = [...parsedRows, row.data[0]]
        }
      },
      complete: () => {
        setCsv(getUserCSVRows(parsedRows))
        setHeaders(getUserCSVHeaders(parsedRows[0], formatMessage))
        setCurrentStep(UpdatePeopleSteps.UPDATE_PEOPLE_HEADERS)
      }
    })
  }

  const submitUsers = () => {
    const updating: User[] = []
    setIsLoading(true)

    csv.forEach((row) => {
      const {errors, user} = csvRowToUser(headers, row, formatMessage)
      if (errors.length === 0) {
        updating.push(user)
      } else {
        user.error = errors
        notUpdated.push(user)
        setNotUpdated(notUpdated)
      }
    })

    const chunks = chunk(updating, 10)
    const funcs = chunks.map((userChunk) => () => {
      const users = uniqBy(userChunk, "email")
      return Promise.all(
        users.map((user) =>
          handleUserUpdate(user).catch((err) => {
            throw err
          })
        )
      )
    })

    return promiseSerialAll(funcs)
      .then(() => {
        if (notUpdated.length > 0) {
          throw new Error()
        }
        setIsLoading(false)
        goBackToCSVStep()
        setShowSuccessModal(true)
      })
      .catch(() => {
        setCurrentStep(UpdatePeopleSteps.UPDATE_PEOPLE_FAILED)
      })
  }

  const handleUserUpdate = (user: User) => {
    removeFromNotUpdated(user)

    if (user.dateOfBirth && user.dateOfBirth.length === 10) {
      const date = moment(user.dateOfBirth, "DD/MM/YYYY")

      if (!date.isValid()) {
        addUserToNotUpdated(user, formatMessage({id: "admin.addMultiplePeople.dateErrorMessage.dobValidation"}))
        return Promise.resolve()
      } else if (date.year() <= 1900 || date.year() >= moment().year()) {
        addUserToNotUpdated(user, formatMessage({id: "admin.addMultiplePeople.dateErrorMessage.dobYearValidation"}))
        return Promise.resolve()
      }
    }

    if (user.dateOfBirth && user.dateOfBirth.length === 5 && !moment(user.dateOfBirth, "DD/MM").isValid()) {
      addUserToNotUpdated(user, formatMessage({id: "admin.addMultiplePeople.dateErrorMessage.dobValidation"}))
      return Promise.resolve()
    }

    if (user.externalStartDate && !moment(user.externalStartDate, "DD/MM/YYYY").isValid()) {
      addUserToNotUpdated(user, formatMessage({id: "admin.addMultiplePeople.dateErrorMessage.esdValidation"}))
      return Promise.resolve()
    }

    return usersService
      .getUserByEmail(user.email!)
      .then(async (existingUser) => {
        return await waitPromise(1000).then(() => {
          return updateUser(user, existingUser)
            .then(() => {
              return true
            })
            .catch((err) => {
              addUserToNotUpdated(user, getErrorMessage(err))
            })
        })
      })
      .catch(() => {
        addUserToNotUpdated(user, formatMessage({id: "admin.addMultiplePeople.dateErrorMessage.emailValidation"}))
      })
  }

  const hasUpdatedFields = (csvUser: User, existingUser: User) => {
    if (
      (headers.find((h) => h === FIRSTNAME) && isValid(csvUser?.firstName) !== existingUser?.firstName) ||
      (headers.find((h) => h === LASTNAME) && isValid(csvUser?.lastName) !== existingUser?.lastName) ||
      (headers.find((h) => h === DATE_OF_BIRTH) && isValid(csvUser?.dateOfBirth) !== getUserDate(existingUser?.dateOfBirth)) ||
      (headers.find((h) => h === EMPLOYMENT_START_DATE) &&
        getEmployementDate(csvUser?.externalStartDate) !== existingUser?.externalStartDate) ||
      (headers.find((h) => h === EXTERNAL_ID) && isValid(csvUser?.externalUserId) !== existingUser?.externalUserId)
    ) {
      return true
    }
    return false
  }

  const isValid = (value) => {
    return value ? value : ""
  }
  const getUserDate = (date?: UserDateOfBirth) => {
    if (date?.day && date?.month) {
      return date?.year
        ? moment([date.year, date.month - 1, date.day]).format("DD/MM/YYYY")
        : moment({month: date.month - 1, day: date.day}).format("DD/MM")
    }
    return ""
  }

  const getEmployementDate = (date) => {
    const includeEmploymentDate = headers.find((h) => h === EMPLOYMENT_START_DATE)
    if (!includeEmploymentDate) {
      return null
    }
    return date ? moment.utc(date, "DD/MM/YYYY").toISOString() : ""
  }

  const updateUser = (data, existingUser) => {
    const includeDob = headers.find((h) => h === DATE_OF_BIRTH)
    const user: UserInput = {
      uuid: existingUser.uuid,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      dateOfBirth: includeDob ? setIntDate(data.dateOfBirth) : undefined,
      employmentStartDate: getEmployementDate(data.externalStartDate),
      externalUserId: data.externalUserId
    }
    if (hasUpdatedFields(data, existingUser)) {
      setUpdatedNo((prevUpdatedNumber) => prevUpdatedNumber + 1)
      return usersService.update({user}).then((updatedUser) => {
        addUserToUpdated(updatedUser)
      })
    }
    return Promise.resolve(null)
  }

  const addUserToUpdated = (user: User) => {
    updated.push(user)
    setUpdated(updated)
  }

  const addUserToNotUpdated = (user: User, error: string) => {
    user.error = [error]
    notUpdated.push(user)
    setNotUpdated(notUpdated)
  }

  const removeFromNotUpdated = (user: User, cb?: () => void) => {
    for (let i = 0; i < notUpdated.length; i++) {
      if (isEqual(user, notUpdated[i])) {
        notUpdated.splice(i, 1)
        break
      }
    }
    setNotUpdated([...notUpdated])
    if (cb) {
      cb()
    }
  }

  const handleOnCloseModal = () => {
    setShowSuccessModal(false)
    setUpdatedNo(0)
  }

  const goBackToCSVStep = () => {
    setCurrentStep(UpdatePeopleSteps.UPDATE_PEOPLE_CSV)
    setNotUpdated([])
    setUpdated([])
  }

  const updateHeader = (value: string, idx: number) => {
    const updatedHeaders = headers.map((header, i) => {
      if (value && value !== DONT_INCLUDE) {
        return header === value ? "" : header
      }
      return header
    })
    updatedHeaders.splice(idx, 1, value)
    setHeaders(updatedHeaders)
  }

  const handleUsersSuccess = () => {
    setShowSuccessModal(true)
    setIsLoading(false)
    goBackToCSVStep()
  }

  return (
    <>
      <BackLink />
      {currentStep === UpdatePeopleSteps.UPDATE_PEOPLE_CSV && <EditPeopleCSV onDrop={handleOnDrop} />}
      {currentStep === UpdatePeopleSteps.UPDATE_PEOPLE_HEADERS && (
        <AddPeopleHeaders
          alignLeft={false}
          headers={headers}
          usersLength={csv.length}
          csv={csv.slice(0, 5)}
          updateHeader={updateHeader}
          isLoading={isLoading}
          submitUsers={submitUsers}
          back={goBackToCSVStep}
          requiredHeaders={[FIRSTNAME, LASTNAME, EMAIL]}
          requiredMessage="admin.components.addPeopleHeaders.headerFour.firstNameLastNameAndEmail"
          buttonText="admin.components.addPeopleHeaders.button.update"
          showRestrictions={false}
        />
      )}
      {currentStep === UpdatePeopleSteps.UPDATE_PEOPLE_FAILED && (
        <EditPeopleFailed
          headers={headers}
          notAdded={notUpdated}
          added={updated}
          handleCreate={handleUserUpdate}
          removeUser={removeFromNotUpdated}
          gotoSuccess={handleUsersSuccess}
          back={goBackToCSVStep}
        />
      )}
      <SuccessModal show={showSuccessModal} users={updatedNo} onClose={handleOnCloseModal} />
    </>
  )
}

export default injectIntl(EditMultiplePeople)
